/**
 * phoneNumber.ts
 *
 * One shared rule for the phone numbers people type on BotSpot forms.
 * Rob, 2026-10-19: one parser everywhere. Rob, 2026-10-21: "I would not want
 * to lose their number ... I also wouldn't want it to be annoying bc then we
 * lose the whole lead. Europe and other countries requiring + might be an
 * issue too no?"
 *
 * WHY THIS EXISTS:
 * - Parse with the same library, version (1.13.13), metadata (max) and
 *   placeholder list as botspot_node src/messaging/phoneIdentity.ts, so
 *   "valid" here means valid on the server.
 * - People type their national number for the country picked beside the
 *   field (07400 123456 with United Kingdom), no + needed. A + number switches
 *   the country itself.
 * - Nothing here blocks a form. An unreadable number is returned exactly as
 *   typed (trimmed) so no path ever drops what someone entered.
 *
 * IMPORTANT BEHAVIORS:
 * - The library (about 40 KB gzipped) loads lazily,
 *   off the landing page's critical path. Until they arrive, US/Canada ten and
 *   eleven digit numbers still normalize through a small built-in rule, and
 *   everything else is left as typed.
 * - Display formatting only tidies a valid number.
 */
import type * as PhoneLibraryModule from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';

type PhoneLibrary = typeof PhoneLibraryModule;
export type PhoneCountry = string;

let phoneLibrary: PhoneLibrary | null = null;
let pendingLoad: Promise<PhoneLibrary | null> | null = null;

/** Starts (or reuses) the lazy load. Resolves null if the chunk cannot load. */
export function loadPhoneLibrary(): Promise<PhoneLibrary | null> {
  if (phoneLibrary) return Promise.resolve(phoneLibrary);
  if (!pendingLoad) {
    pendingLoad = import(/* webpackChunkName: "phone-number" */ 'libphonenumber-js/max')
      .then((module) => {
        phoneLibrary = module;
        return module;
      })
      .catch(() => {
        // A failed chunk must never break a form. Allow a later retry.
        pendingLoad = null;
        return null;
      });
  }
  return pendingLoad;
}

export const isPhoneLibraryLoaded = (): boolean => phoneLibrary !== null;

// Same list the server refuses (botspot_node phoneIdentity.ts).
const PLACEHOLDER_E164 = new Set(['+15555551234', '+19999999999']);
const PHONE_CHARACTERS = /^[+\d\s().-]+$/;
const NANP_TEN_DIGITS = /^[2-9]\d{2}[2-9]\d{6}$/;
const NANP_ELEVEN_DIGITS = /^1[2-9]\d{2}[2-9]\d{6}$/;
const NANP_COUNTRIES = new Set(['US', 'CA']);

/** What a phone field can hold: typed text, a saved value, or nothing yet. */
export type PhoneText = string | null | undefined;

const asText = (value: PhoneText): string => (value == null ? '' : String(value));
const upper = (country: PhoneCountry | null | undefined): string =>
  String(country || 'US').toUpperCase();

/** Used only before the library has loaded: US/Canada shapes, nothing else. */
const fallbackE164 = (trimmed: string, country: string): string | null => {
  if (!PHONE_CHARACTERS.test(trimmed)) return null;
  if (trimmed.startsWith('+')) {
    const digits = trimmed.replace(/\D/g, '');
    return NANP_ELEVEN_DIGITS.test(digits) && !PLACEHOLDER_E164.has(`+${digits}`)
      ? `+${digits}`
      : null;
  }
  if (!NANP_COUNTRIES.has(country)) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (/^(.)\1+$/.test(digits)) return null;
  let e164: string | null = null;
  if (NANP_TEN_DIGITS.test(digits)) e164 = `+1${digits}`;
  else if (NANP_ELEVEN_DIGITS.test(digits)) e164 = `+${digits}`;
  return e164 && !PLACEHOLDER_E164.has(e164) ? e164 : null;
};

/** The library's own country code when it knows the country, otherwise undefined. */
const knownCountry = (country: string): CountryCode | undefined =>
  phoneLibrary && phoneLibrary.isSupportedCountry(country) ? country : undefined;
const supported = (country: string): boolean => knownCountry(country) !== undefined;

/**
 * E.164 (for example +447400123456) when the number is valid, otherwise null.
 * A number without + is read as a national number of `country` (default US).
 */
export function toValidE164(value: PhoneText, country: PhoneCountry = 'US'): string | null {
  const trimmed = asText(value).trim();
  if (!trimmed) return null;
  const region = upper(country);
  if (!phoneLibrary) return fallbackE164(trimmed, region);
  try {
    const parsed = phoneLibrary.parsePhoneNumberWithError(trimmed, {
      defaultCountry: trimmed.startsWith('+') ? undefined : knownCountry(region),
      extract: false,
    });
    if (!parsed.isValid() || PLACEHOLDER_E164.has(parsed.number)) return null;
    return parsed.number;
  } catch {
    return null;
  }
}

/**
 * What a form should send: E.164 when valid, otherwise exactly what was
 * typed (trimmed). Empty stays empty. Never throws, never drops input.
 */
export function normalizePhoneForSubmit(value: PhoneText, country: PhoneCountry = 'US'): string {
  const trimmed = asText(value).trim();
  if (!trimmed) return '';
  return toValidE164(trimmed, country) ?? trimmed;
}

/**
 * Tidy display for a finished entry (on blur, or a saved value). With a
 * country: national format when the number belongs to that country's calling
 * code (the picker shows the +code), international otherwise. Without one:
 * national for +1, international for the rest. Unreadable input is shown
 * exactly as typed.
 */
export function formatPhoneForDisplay(value: PhoneText, country?: PhoneCountry | null): string {
  const text = asText(value);
  const e164 = toValidE164(text, country || 'US');
  if (!e164 || !phoneLibrary) return text;
  try {
    const parsed = phoneLibrary.parsePhoneNumber(e164);
    const known = country ? knownCountry(upper(country)) : undefined;
    const nationalCode = known ? phoneLibrary.getCountryCallingCode(known) : '1';
    return parsed.countryCallingCode === nationalCode
      ? parsed.formatNational()
      : parsed.formatInternational();
  } catch {
    return text;
  }
}

// Where one calling code covers several countries, pick the main one.
const MAIN_COUNTRY_FOR_CODE = new Map([
  ['1', 'US'],
  ['7', 'RU'],
  ['44', 'GB'],
  ['47', 'NO'],
  ['61', 'AU'],
  ['39', 'IT'],
  ['212', 'MA'],
  ['262', 'RE'],
  ['290', 'SH'],
  ['358', 'FI'],
  ['590', 'GP'],
  ['599', 'CW'],
]);

/**
 * The country a + number belongs to, as soon as it can be told. Returns null
 * for text without + or when the calling code is not complete yet.
 */
export function countryFromInternationalInput(value: PhoneText): PhoneCountry | null {
  const trimmed = asText(value).trim();
  if (!trimmed.startsWith('+') || !phoneLibrary) return null;
  try {
    const typer = new phoneLibrary.AsYouType();
    typer.input(trimmed);
    const country = typer.getCountry();
    if (country) return country;
    const code = typer.getCallingCode();
    if (!code) return null;
    const main = MAIN_COUNTRY_FOR_CODE.get(code);
    if (main) return main;
    return (
      phoneLibrary.getCountries().find((c) => phoneLibrary?.getCountryCallingCode(c) === code) ||
      null
    );
  } catch {
    return null;
  }
}

/** The country of a saved or valid value, for showing it with the right picker. */
export function countryOfValidNumber(
  value: PhoneText,
  country: PhoneCountry = 'US',
): PhoneCountry | null {
  const e164 = toValidE164(value, country);
  if (!e164 || !phoneLibrary) return null;
  try {
    const parsed = phoneLibrary.parsePhoneNumber(e164);
    const known = knownCountry(upper(country));
    if (known && parsed.countryCallingCode === phoneLibrary.getCountryCallingCode(known)) {
      return known;
    }
    return parsed.country || MAIN_COUNTRY_FOR_CODE.get(parsed.countryCallingCode) || null;
  } catch {
    return null;
  }
}

// Visitors whose browser language has no region (plain "en") get a country
// from their time zone. Only zones where the answer is unambiguous.
const TIME_ZONE_COUNTRY = new Map([
  ['America/Toronto', 'CA'],
  ['America/Vancouver', 'CA'],
  ['America/Edmonton', 'CA'],
  ['America/Winnipeg', 'CA'],
  ['America/Halifax', 'CA'],
  ['America/St_Johns', 'CA'],
  ['America/Regina', 'CA'],
  ['America/Mexico_City', 'MX'],
  ['America/Sao_Paulo', 'BR'],
  ['America/Argentina/Buenos_Aires', 'AR'],
  ['America/Bogota', 'CO'],
  ['America/Lima', 'PE'],
  ['America/Santiago', 'CL'],
  ['Europe/London', 'GB'],
  ['Europe/Dublin', 'IE'],
  ['Europe/Paris', 'FR'],
  ['Europe/Berlin', 'DE'],
  ['Europe/Madrid', 'ES'],
  ['Europe/Rome', 'IT'],
  ['Europe/Amsterdam', 'NL'],
  ['Europe/Brussels', 'BE'],
  ['Europe/Zurich', 'CH'],
  ['Europe/Vienna', 'AT'],
  ['Europe/Stockholm', 'SE'],
  ['Europe/Oslo', 'NO'],
  ['Europe/Copenhagen', 'DK'],
  ['Europe/Helsinki', 'FI'],
  ['Europe/Warsaw', 'PL'],
  ['Europe/Prague', 'CZ'],
  ['Europe/Lisbon', 'PT'],
  ['Europe/Athens', 'GR'],
  ['Europe/Istanbul', 'TR'],
  ['Europe/Kiev', 'UA'],
  ['Europe/Kyiv', 'UA'],
  ['Europe/Bucharest', 'RO'],
  ['Europe/Budapest', 'HU'],
  ['Africa/Lagos', 'NG'],
  ['Africa/Johannesburg', 'ZA'],
  ['Africa/Nairobi', 'KE'],
  ['Africa/Cairo', 'EG'],
  ['Africa/Accra', 'GH'],
  ['Asia/Kolkata', 'IN'],
  ['Asia/Calcutta', 'IN'],
  ['Asia/Shanghai', 'CN'],
  ['Asia/Hong_Kong', 'HK'],
  ['Asia/Singapore', 'SG'],
  ['Asia/Tokyo', 'JP'],
  ['Asia/Seoul', 'KR'],
  ['Asia/Manila', 'PH'],
  ['Asia/Dubai', 'AE'],
  ['Asia/Karachi', 'PK'],
  ['Asia/Jakarta', 'ID'],
  ['Asia/Bangkok', 'TH'],
  ['Asia/Jerusalem', 'IL'],
  ['Australia/Sydney', 'AU'],
  ['Australia/Melbourne', 'AU'],
  ['Australia/Brisbane', 'AU'],
  ['Australia/Perth', 'AU'],
  ['Australia/Adelaide', 'AU'],
  ['Pacific/Auckland', 'NZ'],
]);

/**
 * The visitor's likely country: the region of their first browser language
 * that has one (en-GB -> GB), else their time zone, else US.
 */
export function guessVisitorCountry(
  languages: readonly string[] | undefined = globalThis.navigator?.languages ??
    (globalThis.navigator?.language ? [globalThis.navigator.language] : []),
  timeZone: string | undefined = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  })(),
): PhoneCountry {
  for (const language of languages || []) {
    const match = /^[a-z]{2,3}(?:-[A-Za-z]{4})?-([A-Za-z]{2})\b/.exec(String(language));
    if (match) return match[1].toUpperCase();
  }
  const zoneCountry = timeZone ? TIME_ZONE_COUNTRY.get(timeZone) : undefined;
  if (zoneCountry) return zoneCountry;
  return 'US';
}

/** Flag emoji for an ISO country code (shown as two letters where unsupported). */
export function countryFlag(country: PhoneCountry): string {
  return upper(country).replace(/./g, (letter) =>
    String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65),
  );
}

export type PhoneCountryOption = {
  code: PhoneCountry;
  name: string;
  callingCode: string;
  flag: string;
};

let countryOptionsCache: PhoneCountryOption[] | null = null;

/** Every country the library knows, by name. Empty until the library loads. */
export function listPhoneCountries(): PhoneCountryOption[] {
  if (!phoneLibrary) return [];
  if (countryOptionsCache) return countryOptionsCache;
  let names: { of: (code: string) => string | undefined } | null = null;
  try {
    names = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    names = null;
  }
  const library = phoneLibrary;
  countryOptionsCache = library
    .getCountries()
    .map((code) => ({
      code,
      name: names?.of(code) || code,
      callingCode: library.getCountryCallingCode(code),
      flag: countryFlag(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return countryOptionsCache;
}

/** +44 for GB. Empty until the library loads, except US/Canada (+1). */
export function callingCodeFor(country: PhoneCountry): string {
  const region = upper(country);
  const known = knownCountry(region);
  if (phoneLibrary && known) return phoneLibrary.getCountryCallingCode(known);
  return NANP_COUNTRIES.has(region) ? '1' : '';
}
